import React, { useState, useEffect, useRef } from "react";
import { MicOutline, Stop, RefreshOutline, CheckmarkCircle, VolumeHighOutline } from "react-ionicons";

const PronunciationPractice = (props) => {
    const { data, markedAsDone } = props;
    const moduloactual = data.moduloactual;

    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState("");
    const [score, setScore] = useState(moduloactual.hecho ? 100 : 0);
    const [hasPassed, setHasPassed] = useState(moduloactual.hecho === true || moduloactual.hecho === 1);
    const [recognitionError, setRecognitionError] = useState("");
    const [phonetics, setPhonetics] = useState({});

    useEffect(() => {
        // Pre-load voices on component mount
        window.speechSynthesis.getVoices();
    }, []);

    const recognitionRef = useRef(null);

    // Extract target text
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = moduloactual.cursos_item.descripcion;
    const rawTargetText = tempDiv.textContent || tempDiv.innerText || "";
    // Clean text: keep only letters, numbers and spaces for easier matching
    const cleanWord = (word) => word.toLowerCase().replace(/[^a-z0-9]/g, "");

    const targetWords = rawTargetText.split(/\s+/).filter((w) => w.length > 0);
    const targetWordsClean = targetWords.map(cleanWord);

    useEffect(() => {
        const fetchPhonetics = async () => {
            for (const word of targetWordsClean) {
                try {
                    let phoneticText = "";
                    try {
                        const res = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${word}`);
                        if (res.ok) {
                            const json = await res.json();
                            if (json && json.length > 0) {
                                phoneticText = json[0].phonetic;
                                if (!phoneticText && json[0].phonetics) {
                                    const p = json[0].phonetics.find(x => x.text);
                                    if (p) phoneticText = p.text;
                                }
                            }
                        }
                    } catch (e1) {
                        // ignore and try fallback
                    }

                    if (!phoneticText) {
                        const res2 = await fetch(`https://rhymebrain.com/talk?function=getWordInfo&word=${word}`);
                        if (res2.ok) {
                            const json2 = await res2.json();
                            if (json2 && json2.ipa) {
                                phoneticText = `/${json2.ipa}/`;
                            }
                        }
                    }

                    if (phoneticText) {
                        setPhonetics(prev => ({ ...prev, [word]: phoneticText }));
                    }
                } catch (e) {
                    console.log("Phonetics arr", e);
                }
            }
        };
        fetchPhonetics();
    }, [rawTargetText]);

    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            setRecognitionError("Tu navegador no soporta el reconocimiento de voz. Te recomendamos usar Google Chrome.");
            return;
        }

        const recognition = new SpeechRecognition();
        const langCode = moduloactual.cursos_item.html || "en-US";
        recognition.lang = langCode;
        recognition.interimResults = true;
        recognition.continuous = true;

        recognition.onresult = (event) => {
            let finalTranscript = "";
            let interimTranscript = "";
            for (let i = 0; i < event.results.length; i++) {
                if (event.results[i].isFinal) {
                    finalTranscript += event.results[i][0].transcript + " ";
                } else {
                    interimTranscript += event.results[i][0].transcript + " ";
                }
            }
            setTranscript(finalTranscript + interimTranscript);
        };

        recognition.onerror = (event) => {
            console.error("Speech recognition error", event.error);
            setIsListening(false);
        };

        recognition.onend = () => {
            setIsListening(false);
        };

        recognitionRef.current = recognition;

        // Cleanup
        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, []);

    const toggleListening = () => {
        if (isListening) {
            recognitionRef.current?.stop();
            setIsListening(false);
        } else {
            setTranscript("");
            setScore(0);
            setRecognitionError("");
            try {
                recognitionRef.current?.start();
                setIsListening(true);
            } catch (err) {
                console.error(err);
            }
        }
    };

    const resetPractice = () => {
        if (isListening) {
            recognitionRef.current?.stop();
            setIsListening(false);
        }
        setTranscript("");
        setScore(0);
    };

    useEffect(() => {
        if (!targetWordsClean.length || transcript.length === 0) return;

        const spokenWords = transcript.split(/\s+/).filter((w) => w.length > 0).map(cleanWord);
        
        let matches = 0;
        let spokenIndex = 0;

        for (let i = 0; i < targetWordsClean.length; i++) {
            let found = false;
            for (let j = spokenIndex; j < spokenWords.length; j++) {
                if (targetWordsClean[i] === spokenWords[j]) {
                    matches++;
                    spokenIndex = j + 1;
                    found = true;
                    break;
                }
            }
        }

        const currentScore = Math.floor((matches / targetWordsClean.length) * 100);
        setScore(currentScore);

        if (currentScore >= 80 && !hasPassed) {
            setHasPassed(true);
            setTimeout(() => {
                 markedAsDone(moduloactual.id);
            }, 1500);
        }
    }, [transcript]);

    const renderTextWithFeedback = () => {
        const spokenWords = transcript.split(/\s+/).filter((w) => w.length > 0).map(cleanWord);
        let spokenIndex = 0;

        return targetWords.map((word, index) => {
            const cleanTarget = targetWordsClean[index];
            let isMatched = false;

            for (let j = spokenIndex; j < spokenWords.length; j++) {
                if (cleanTarget === spokenWords[j]) {
                    isMatched = true;
                    spokenIndex = j + 1;
                    break;
                }
            }

            let colorStatus = "text-secondary"; // Default unread
            if (transcript.length > 0) {
                 colorStatus = isMatched ? "text-success font-weight-bold" : "text-danger";
            }

            return (
                <div key={index} className="d-inline-flex flex-column align-items-center justify-content-center mr-2 mb-3" style={{minWidth: "40px"}}>
                    <span className={`${colorStatus}`} style={{ fontSize: "2.2rem", transition: "color 0.3s" }}>
                        {word}
                    </span>
                    {phonetics[cleanTarget] ? (
                        <small className="text-secondary" style={{ fontSize: "1rem", opacity: 0.8 }}>
                            {phonetics[cleanTarget]}
                        </small>
                    ) : (
                        <small className="text-secondary" style={{ fontSize: "1rem", opacity: 0, userSelect: 'none' }}>
                            ...
                        </small>
                    )}
                </div>
            );
        });
    };

    return (
        <div className="card shadow-sm mb-4">
            <div className="card-body text-center p-4">
                <h4 className="mb-4">Práctica de Pronunciación</h4>
                
                {recognitionError && (
                    <div className="alert alert-danger">{recognitionError}</div>
                )}
                
                <div className="mb-4 p-4 bg-light rounded" style={{ minHeight: "150px", lineHeight: "2" }}>
                    {renderTextWithFeedback()}
                </div>

                <div className="d-flex justify-content-center align-items-center gap-3">
                    <button
                        className="btn btn-outline-info rounded-circle p-3 d-flex justify-content-center align-items-center mr-3"
                        onClick={() => {
                            if (!('speechSynthesis' in window)) {
                                alert("Tu navegador no soporta lectura de texto.");
                                return;
                            }
                            const utterance = new SpeechSynthesisUtterance(rawTargetText);
                            utterance.lang = moduloactual.cursos_item.html || "en-US";
                            let voices = window.speechSynthesis.getVoices();
                            if (voices.length === 0) {
                                // Sometimes Chrome requires a delay to load voices
                                setTimeout(() => {
                                    voices = window.speechSynthesis.getVoices();
                                    playWithVoices(voices);
                                }, 50);
                            } else {
                                playWithVoices(voices);
                            }

                            function playWithVoices(loadedVoices) {
                                // Prefer Google US English or explicitly local premium voices, avoid Zira (robotic).
                                let premiumVoice = loadedVoices.find(v => v.lang.includes("en") && (v.name.includes("Google") || v.name.includes("Siri") || v.name.includes("Natural")));
                                if (!premiumVoice) {
                                    premiumVoice = loadedVoices.find(v => v.lang.includes("en") && !v.name.includes("Zira"));
                                }
                                if(premiumVoice) {
                                    utterance.voice = premiumVoice;
                                }
                                utterance.rate = 0.85; // Slightly slower feels more natural
                                window.speechSynthesis.cancel();
                                window.speechSynthesis.speak(utterance);
                            }
                        }}
                        style={{ width: "60px", height: "60px" }}
                        title="Escuchar pronunciación"
                    >
                        <VolumeHighOutline color={"#17a2b8"} height="30px" width="30px" />
                    </button>

                    {!isListening ? (
                        <button
                            className="btn btn-primary rounded-circle p-3 d-flex justify-content-center align-items-center mr-3"
                            onClick={toggleListening}
                            style={{ width: "70px", height: "70px" }}
                        >
                            <MicOutline color={"#fff"} height="35px" width="35px" />
                        </button>
                    ) : (
                        <button
                            className="btn btn-danger rounded-circle p-3 d-flex justify-content-center align-items-center mr-3"
                            onClick={toggleListening}
                            style={{ width: "70px", height: "70px" }}
                        >
                            <Stop color={"#fff"} height="35px" width="35px" />
                        </button>
                    )}

                    <button
                        className="btn btn-outline-secondary rounded-circle p-3 d-flex justify-content-center align-items-center"
                        onClick={resetPractice}
                        style={{ width: "50px", height: "50px" }}
                        title="Reiniciar"
                    >
                        <RefreshOutline color={"#6c757d"} height="25px" width="25px" />
                    </button>
                </div>

                <div className="mt-4">
                    <h5>
                        Precisión: <span className={score >= 80 ? "text-success" : "text-primary"}>{score}%</span>
                    </h5>
                    <div className="progress mx-auto" style={{ height: "10px", width: "50%" }}>
                        <div 
                            className={`progress-bar ${score >= 80 ? "bg-success" : "bg-primary"}`} 
                            role="progressbar" 
                            style={{ width: `${score}%`, transition: "width 0.5s" }} 
                            aria-valuenow={score} 
                            aria-valuemin="0" 
                            aria-valuemax="100"
                        ></div>
                    </div>
                </div>

                {hasPassed && (
                    <div className="alert alert-success mt-4 d-inline-block">
                        <CheckmarkCircle color="green" height="25px" width="25px" className="mr-2 mb-1" />
                        ¡Excelente pronunciación! Has completado esta actividad.
                        {moduloactual.hecho ? "" : " (Guardando tu progreso...)"}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PronunciationPractice;
